import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { Filter, Search, X } from "lucide-react";
import { useMemo, useState } from "react";

import { Eyebrow } from "@/components/dravian/eyebrow";
import { WorkspaceShell } from "@/components/dravian/workspace-shell";
import { entities as entitiesApi, ontologies as ontologiesApi, type ApiEntity, type ApiOntology } from "@/lib/api";
import { colorForType } from "@/lib/entity-color";
import { getDomainHints } from "@/lib/domain-hints";
import { useWorkspaces } from "@/store/workspaces";

export const Route = createFileRoute("/$workspace/entities")({
  head: () => ({ meta: [{ title: "Entities — Dravian Horizon" }] }),
  component: EntitiesPage,
});

function EntitiesPage() {
  const { workspace: id } = useParams({ from: "/$workspace/entities" });
  const navigate = useNavigate();
  const { getById } = useWorkspaces();
  const ws = getById(id);

  const entsQ = useQuery({
    queryKey: ["entities", id],
    queryFn: () => entitiesApi.list(id),
  });
  const ontsQ = useQuery({
    queryKey: ["ontologies"],
    queryFn: ontologiesApi.list,
  });

  const ontology: ApiOntology | undefined = useMemo(
    () => (ontsQ.data ?? []).find((o) => o.id === ws?.ontology_id),
    [ontsQ.data, ws?.ontology_id],
  );

  const [search, setSearch] = useState("");
  const [type, setType] = useState<string | null>(null);

  const entities = entsQ.data ?? [];

  const typeFacets = useMemo(() => {
    const counts = new Map<string, number>();
    for (const e of entities) counts.set(e.type, (counts.get(e.type) ?? 0) + 1);
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  }, [entities]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return entities
      .filter((e) => (type ? e.type === type : true))
      .filter((e) => {
        if (!q) return true;
        if (e.name.toLowerCase().includes(q)) return true;
        if (e.type.toLowerCase().includes(q)) return true;
        for (const v of Object.values(e.attributes ?? {})) {
          if (typeof v === "string" && v.toLowerCase().includes(q)) return true;
        }
        return false;
      })
      .sort(
        (a, b) =>
          new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
      );
  }, [entities, type, search]);

  const example = getDomainHints(ws?.domain).entityHint;

  const openEntity = (entityId: string) =>
    navigate({
      to: ".",
      search: (prev: Record<string, unknown>) => ({ ...prev, entity: entityId }),
    });

  return (
    <WorkspaceShell>
      <div className="p-5 max-w-[1200px] mx-auto space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="label-eyebrow mb-1">Entities</div>
            <h2 className="text-[20px] font-semibold">
              {entsQ.isLoading ? "Loading…" : `${entities.length} entities`}
            </h2>
            {ws?.domain && (
              <p className="text-[12px] text-muted-foreground mt-1">
                {ws.domain.replace("_", " ")} workspace
                {ontology && ` · ${ontology.entity_types.length} types in ontology`}
              </p>
            )}
          </div>
          <div className="text-right text-[11px]">
            <div className="font-mono text-[18px] font-semibold text-foreground/95">
              {filtered.length}
              {filtered.length !== entities.length && (
                <span className="text-muted-foreground"> / {entities.length}</span>
              )}
            </div>
            <div className="text-muted-foreground uppercase tracking-wider">Showing</div>
          </div>
        </div>

        <div className="panel p-3 space-y-2.5">
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, type, or attribute…"
              className="flex-1 bg-transparent text-[12px] outline-none placeholder:text-muted-foreground/70"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="h-3 w-3" />
              </button>
            )}
            {(type || search) && (
              <button
                onClick={() => {
                  setType(null);
                  setSearch("");
                }}
                className="rounded border border-border bg-surface px-2 py-1 text-[10px] text-muted-foreground hover:text-foreground"
              >
                Clear
              </button>
            )}
          </div>

          {typeFacets.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap pt-1 border-t border-border">
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground mr-1">
                <Filter className="h-3 w-3" /> Type
              </span>
              <button
                onClick={() => setType(null)}
                className={`rounded px-2 py-0.5 text-[11px] transition ${
                  type == null
                    ? "bg-primary/20 text-primary"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                All
              </button>
              {typeFacets.map(([t, n]) => {
                const active = type === t;
                const color = colorForType(t);
                return (
                  <button
                    key={t}
                    onClick={() => setType(t)}
                    className={`inline-flex items-center gap-1.5 rounded px-2 py-0.5 text-[11px] transition ${
                      active
                        ? "bg-primary/20 text-primary"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <span
                      className="h-1.5 w-1.5 rounded-full"
                      style={{ background: color }}
                    />
                    <span className="capitalize">{t.replace(/_/g, " ")}</span>
                    <span className="font-mono text-muted-foreground/70">{n}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {entsQ.isLoading ? (
          <div className="panel p-6 text-center text-[12px] text-muted-foreground">
            Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div className="panel p-6 text-center text-[12px] text-muted-foreground">
            {entities.length === 0 ? (
              <>
                No entities yet. Use the chat panel to add one — try{" "}
                <span className="text-foreground/85 italic">{example}</span>.
              </>
            ) : (
              "No entities match the current filters."
            )}
          </div>
        ) : (
          <div className="panel overflow-hidden">
            <table className="w-full text-[13px]">
              <thead className="bg-surface-elevated/50 text-[10px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="text-left px-4 py-2.5">Name</th>
                  <th className="text-left px-4 py-2.5">Type</th>
                  <th className="text-left px-4 py-2.5">Attributes</th>
                  <th className="text-left px-4 py-2.5">Region</th>
                  <th className="text-right px-4 py-2.5">Source</th>
                  <th className="text-right px-4 py-2.5">Updated</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((e) => (
                  <EntityRow key={e.id} entity={e} onOpen={() => openEntity(e.id)} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </WorkspaceShell>
  );
}

function EntityRow({
  entity,
  onOpen,
}: {
  entity: ApiEntity;
  onOpen: () => void;
}) {
  const color = colorForType(entity.type);
  const attrs = entity.attributes ?? {};
  const attrPreview = Object.entries(attrs)
    .filter(([, v]) => v !== null && v !== undefined && v !== "")
    .slice(0, 3)
    .map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`)
    .join(" · ");
  const region = entity.region ?? null;
  return (
    <tr
      onClick={onOpen}
      className="border-t border-border cursor-pointer hover:bg-surface-elevated/40"
    >
      <td className="px-4 py-3 font-medium">
        <span className="inline-flex items-center gap-2">
          <span
            className="h-1.5 w-1.5 rounded-full shrink-0"
            style={{ background: color }}
          />
          {entity.name}
        </span>
      </td>
      <td className="px-4 py-3 text-muted-foreground capitalize">
        {entity.type.replace(/_/g, " ")}
      </td>
      <td className="px-4 py-3 text-[11px] text-muted-foreground font-mono truncate max-w-[260px]">
        {attrPreview || "—"}
      </td>
      <td className="px-4 py-3 text-muted-foreground">{region ?? "—"}</td>
      <td className="px-4 py-3 text-right text-[11px] text-muted-foreground font-mono">
        {entity.source}
      </td>
      <td className="px-4 py-3 text-right text-[11px] text-muted-foreground">
        {relative(entity.updated_at)}
      </td>
    </tr>
  );
}

function relative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return "just now";
  const mins = Math.round(ms / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.round(days / 30)}mo ago`;
}
